import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout } from '@/api/platform/auth';

interface User {
  id: number;
  loginName: string;
  name: string;
  email: string;
  headImage: string | null;
  userSn: string;
  permCodes: string[];
  superAdmin: string;
  userType: string;
  orgType: string;
  orgSn: string;
  mobile: string;
  mustChangePwd: string;
}

interface LoginResponse {
  data: {
    access_token: string;
    refresh_token?: string;
    auth_user: any;
  };
}

interface UserResponse {
  data: User;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (loginName: string, password: string, identifyCode: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  updateUser: (partial: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'opc_user';

function getStoredUser(): User | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredUser);
  const [hasToken, setHasToken] = useState(() => !!sessionStorage.getItem('token'));

  const login = useCallback(async (loginName: string, password: string, identifyCode: string) => {
    if (!loginName.trim() || !password.trim()) {
      return { ok: false, error: '请输入账号和密码' };
    }
    // if (!identifyCode.trim()) {
    //   return { ok: false, error: '请输入邮箱验证码' };
    // }

    try {
      // 调用登录 API
      const loginRes = (await apiLogin({ username: loginName.trim(), password })) as LoginResponse;
      const { access_token, refresh_token, auth_user } = loginRes.data;
     
      // 存储 token 到 sessionStorage（减少 XSS 风险）
      sessionStorage.setItem('token', access_token);
      setHasToken(true);
      if (refresh_token) {
        sessionStorage.setItem('refreshToken', refresh_token);
      }

      // 获取完整用户信息
      const meRes = {
        data:{ 
          id: auth_user.userid, 
          loginName: auth_user.username, 
          name: auth_user.userNameCN, 
          email: auth_user.email, 
          headImage: auth_user.headImage, 
          userSn: auth_user.userSn, 
          permCodes: auth_user.permCodes, 
          superAdmin: auth_user.superAdmin,
          mustChangePwd: auth_user.mustChangePwd,
        }
     } as UserResponse;
     sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meRes.data));
     setUser(meRes.data);

      return { ok: true };
    } catch (err: unknown) {
      // 登录失败时清除可能已存入的 token
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem(STORAGE_KEY);
      setHasToken(false);
      const errorMsg = err instanceof Error ? err.message : '账号或密码错误';
      return { ok: false, error: errorMsg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // 即使登出 API 失败也继续清理本地状态
    }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setHasToken(false);
  }, []);

  // 监听全局 auth:unauthorized 事件
  useEffect(() => {
    const handleUnauthorized = () => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      setUser(null);
      setHasToken(false);
      window.location.href = '/login';
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: hasToken, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
